import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  CartProductInput,
  useCart,
} from "../contexts/CartContext";
import { productApi } from "../api/productApi";
import { getImageUrl } from "../utils/getImageUrl";

import "../styles/ProductDetailPage.css";

// PRODUCT_DETAIL_SAFE_VERSION_3

type ProductImage = {
  url: string;
  fileName?: string;
  alt: string;
  isMain: boolean;
  sortOrder: number;
};

type ProductSpecification = {
  label: string;
  value: string;
};

type Product = {
  id: string;
  name: string;
  slug: string;
  sku: string;
  parentCategoryId?: string;
  parentCategoryName?: string;
  categoryId?: string;
  categoryName: string;
  price: number;
  salePrice: number;
  shortDescription: string;
  description: string;
  images: ProductImage[];
  specifications: ProductSpecification[];
  tags: string[];
  seo?: {
    title?: string;
    description?: string;
    keywords?: string[];
    canonicalUrl?: string;
    ogImage?: string;
  };
  visibility: {
    isPublished: boolean;
    showOnMenu: boolean;
  };
  status: "active" | "draft" | "inactive";
};

const ZALO_URL = "https://zalo.me/0918229999";

const careCards = [
  {
    number: "01",
    title: "Tư vấn đúng nhu cầu",
    text: "Đội ngũ trao đổi về mẫu, size, màu sắc và mục đích sử dụng trước khi khách quyết định.",
  },
  {
    number: "02",
    title: "Xem hình ảnh thực tế",
    text: "Khách có thể yêu cầu thêm hình ảnh hoặc video ở nhiều điều kiện ánh sáng.",
  },
  {
    number: "03",
    title: "Hỗ trợ chọn size",
    text: "Đối chiếu cổ tay, bàn tay và thói quen đeo để chọn kích thước phù hợp.",
  },
  {
    number: "04",
    title: "Chăm sóc sau mua",
    text: "Hướng dẫn bảo quản, vệ sinh và tiếp nhận nhu cầu hỗ trợ sau khi bàn giao.",
  },
];

const afterSaleSteps = [
  {
    number: "01",
    title: "Liên hệ",
    text: "Gửi hình ảnh, mã sản phẩm hoặc mô tả nhu cầu qua Zalo và form tư vấn.",
  },
  {
    number: "02",
    title: "Tiếp nhận",
    text: "Chuyên viên kiểm tra thông tin sản phẩm, tình trạng và yêu cầu cần hỗ trợ.",
  },
  {
    number: "03",
    title: "Tư vấn xử lý",
    text: "Đề xuất hướng chăm sóc, vệ sinh, kiểm tra hoặc chính sách phù hợp.",
  },
  {
    number: "04",
    title: "Bàn giao",
    text: "Hoàn tất hỗ trợ kèm hướng dẫn bảo quản và thông tin cần lưu ý.",
  },
];

const formatPrice = (value: number) => {
  if (!value || value <= 0) {
    return "Giá liên hệ";
  }

  const formattedPrice =
    new Intl.NumberFormat("vi-VN").format(
      value
    );

  const priceParts =
    formattedPrice.split(".");

  const firstPart =
    priceParts[0] || "";

  const maskedFirstPart =
    firstPart.length > 1
      ? `${firstPart.charAt(0)}${"x".repeat(
          firstPart.length - 1
        )}`
      : firstPart;

  const maskedPrice = [
    maskedFirstPart,
    ...priceParts
      .slice(1)
      .map(() => "xxx"),
  ].join(".");

  return `${maskedPrice} đ`;
};

const getImageSource = (
  image?: ProductImage | null
) => {
  return image?.url
    ? getImageUrl(image.url)
    : "";
};

const getMainProductImage = (
  product?: Product | null
) => {
  return (
    product?.images?.find(
      (image) => image.isMain
    ) ||
    product?.images?.[0] ||
    null
  );
};

const BackIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M19 12H5" />
    <path d="m10 7-5 5 5 5" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="m14 7 5 5-5 5" />
  </svg>
);

const BagIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5.5 8.5h13l-1 11h-11l-1-11Z" />
    <path d="M9 9V6.8a3 3 0 0 1 6 0V9" />
  </svg>
);

const MessageIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 5.5h16v11H9l-5 3v-14Z" />
    <path d="M8 9h8M8 13h5" />
  </svg>
);

const RulerIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m4 17 13-13 3 3-13 13H4v-3Z" />
    <path d="m14 7 3 3M11 10l2 2M8 13l3 3" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3 5.5 5.6v5.8c0 4.1 2.7 7.7 6.5 9.6 3.8-1.9 6.5-5.5 6.5-9.6V5.6L12 3Z" />
    <path d="m9.3 12 1.8 1.8 3.9-4" />
  </svg>
);

const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{
    slug: string;
  }>();

  const location = useLocation();
  const { addToCart } = useCart();

  const navigationProduct = useMemo(() => {
    const state = location.state as
      | { productPreview?: Product }
      | null;

    const preview = state?.productPreview;

    if (!preview || preview.slug !== slug) {
      return null;
    }

    return preview;
  }, [location.state, slug]);

  const [product, setProduct] =
    useState<Product | null>(() =>
      navigationProduct
    );
  const [selectedImage, setSelectedImage] =
    useState<ProductImage | null>(() =>
      getMainProductImage(navigationProduct)
    );
  const [loading, setLoading] =
    useState(() => !navigationProduct);
  const [message, setMessage] =
    useState("");
  const [addedMessage, setAddedMessage] =
    useState("");

  useEffect(() => {
    let mounted = true;
    const previousTitle = document.title;

    if (navigationProduct) {
      setProduct(navigationProduct);
      setSelectedImage(
        getMainProductImage(navigationProduct)
      );
      setLoading(false);
      setMessage("");

      document.title =
        navigationProduct.seo?.title ||
        `${navigationProduct.name} | King Jades`;
    }

    const fetchProductDetail = async () => {
      if (!slug) {
        if (mounted) {
          setMessage(
            "Không tìm thấy đường dẫn sản phẩm."
          );
          setLoading(false);
        }
        return;
      }

      try {
        if (!navigationProduct) {
          setLoading(true);
        }

        setMessage("");
        setAddedMessage("");

        const data =
          await productApi.getPublicProductDetail(
            slug
          );

        const productData =
          data?.product as
            | Product
            | null
            | undefined;

        if (!productData) {
          throw new Error(
            "API không trả về dữ liệu sản phẩm."
          );
        }

        if (!mounted) return;

        setProduct(productData);
        setSelectedImage(
          getMainProductImage(productData)
        );

        document.title =
          productData.seo?.title ||
          `${productData.name} | King Jades`;
      } catch (requestError) {
        if (!mounted) return;

        const error = requestError as Error;

        setMessage(
          error.message ||
            "Không thể tải chi tiết sản phẩm."
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchProductDetail();

    return () => {
      mounted = false;
      document.title = previousTitle;
    };
  }, [navigationProduct, slug]);

  const sortedImages = useMemo(() => {
    if (!product?.images?.length) {
      return [];
    }

    return [...product.images].sort(
      (first, second) => {
        if (
          first.isMain &&
          !second.isMain
        ) {
          return -1;
        }

        if (
          !first.isMain &&
          second.isMain
        ) {
          return 1;
        }

        return (
          (first.sortOrder || 0) -
          (second.sortOrder || 0)
        );
      }
    );
  }, [product]);

  const finalPrice = useMemo(() => {
    if (!product) return 0;

    return product.salePrice &&
      product.salePrice > 0
      ? product.salePrice
      : product.price || 0;
  }, [product]);

  const discountPercent = useMemo(() => {
    if (
      !product ||
      product.salePrice <= 0 ||
      product.price <= 0 ||
      product.salePrice >= product.price
    ) {
      return 0;
    }

    return Math.round(
      ((product.price -
        product.salePrice) /
        product.price) *
        100
    );
  }, [product]);

  const contactUrl = useMemo(() => {
    if (!product) {
      return "/contact";
    }

    const params = new URLSearchParams({
      productId: product.id,
      productName: product.name,
      productSlug: product.slug,
    });

    return `/contact?${params.toString()}`;
  }, [product]);

  if (loading && !product) {
    return (
      <>
        <Header />

        <div className="kj-product-detail-page">
          <main className="kj-product-detail-main">
            <section className="kj-pdp-state">
              <div className="kj-pdp-spinner" />

              <span className="kj-pdp-eyebrow">
                King Jades Collection
              </span>

              <h1>
                Đang tải chi tiết sản phẩm...
              </h1>
            </section>
          </main>
        </div>

        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Header />

        <div className="kj-product-detail-page">
          <main className="kj-product-detail-main">
            <section className="kj-pdp-state kj-pdp-state--error">
              <span className="kj-pdp-eyebrow">
                Product Not Found
              </span>

              <h1>
                Không thể tải sản phẩm
              </h1>

              <p>
                {message ||
                  "Dữ liệu sản phẩm không tồn tại hoặc API không trả về sản phẩm."}
              </p>

              <Link to="/products">
                <BackIcon />
                Quay lại bộ sưu tập
              </Link>
            </section>
          </main>
        </div>

        <Footer />
      </>
    );
  }

  const findSpecification = (
    keywords: string[]
  ) => {
    if (!product?.specifications?.length) {
      return "";
    }

    const specification =
      product.specifications.find((item) => {
        const label =
          item.label.toLowerCase();

        return keywords.some((keyword) =>
          label.includes(
            keyword.toLowerCase()
          )
        );
      });

    return specification?.value || "";
  };

  const certificationText =
    findSpecification([
      "kiểm định",
      "chứng nhận",
      "certificate",
      "certification",
    ]) ||
    "Liên hệ để kiểm tra hồ sơ áp dụng cho sản phẩm này.";

  // =====================================================
  // SPECIFICATIONS DYNAMIC
  // Admin nhập gì -> ProductDetail hiển thị đúng
  // =====================================================

  const dynamicSpecifications =
    product.specifications?.filter(
      (item) =>
        item.label?.trim() &&
        item.value?.trim()
    ) || [];

  const sizeText =
    findSpecification([
      "size",
      "kích thước",
      "đường kính",
      "chu vi",
    ]) || "Liên hệ tư vấn size";

  const buildCartProduct = (
    productData: Product
  ): CartProductInput => {
    const image =
      selectedImage ||
      productData.images?.find(
        (item) => item.isMain
      ) ||
      productData.images?.[0] ||
      null;

    return {
      id: productData.id,
      name: productData.name,
      slug: productData.slug,
      sku: productData.sku,
      categoryName:
        productData.categoryName,
      price: productData.price,
      salePrice: productData.salePrice,
      image: getImageSource(image),
      shortDescription:
        productData.shortDescription,
    };
  };

  const handleAddToCart = () => {
    if (!product) return;

    addToCart(
      buildCartProduct(product),
      1
    );

    setAddedMessage(
      "Đã thêm sản phẩm vào giỏ quan tâm."
    );

    window.setTimeout(
      () => setAddedMessage(""),
      2600
    );
  };

  return (
    <>
      <Header />

      <div className="kj-product-detail-page">
        <main className="kj-product-detail-main">
          {loading && (
            <section className="kj-pdp-state">
              <div className="kj-pdp-spinner" />

              <span className="kj-pdp-eyebrow">
                King Jades Collection
              </span>

              <h1>
                Đang tải chi tiết sản phẩm...
              </h1>
            </section>
          )}

          {!loading && message && (
            <section className="kj-pdp-state kj-pdp-state--error">
              <span className="kj-pdp-eyebrow">
                Product Not Found
              </span>

              <h1>
                Không thể tải sản phẩm
              </h1>

              <p>{message}</p>

              <Link to="/products">
                <BackIcon />
                Quay lại bộ sưu tập
              </Link>
            </section>
          )}

          {!loading &&
            !message &&
            product && (
              <>
                <section className="kj-pdp-hero">
                  <div
                    className="kj-pdp-hero__glow kj-pdp-hero__glow--left"
                    aria-hidden="true"
                  />
                  <div
                    className="kj-pdp-hero__glow kj-pdp-hero__glow--right"
                    aria-hidden="true"
                  />

                  <div className="kj-pdp-shell">
                    <nav
                      className="kj-pdp-breadcrumb"
                      aria-label="Điều hướng sản phẩm"
                    >
                      <Link to="/">
                        Trang chủ
                      </Link>
                      <span>/</span>
                      <Link to="/products">
                        Sản phẩm
                      </Link>

                      {product.parentCategoryName && (
                        <>
                          <span>/</span>
                          <Link
                            to={
                              product.parentCategoryId
                                ? `/products?parentCategoryId=${encodeURIComponent(
                                    product.parentCategoryId
                                  )}`
                                : `/products?parentCategory=${encodeURIComponent(
                                    product.parentCategoryName
                                  )}`
                            }
                          >
                            {
                              product.parentCategoryName
                            }
                          </Link>
                        </>
                      )}

                      {product.categoryName && (
                        <>
                          <span>/</span>
                          <Link
                            to={
                              product.categoryId
                                ? `/products?categoryId=${encodeURIComponent(
                                    product.categoryId
                                  )}${
                                    product.parentCategoryId
                                      ? `&parentCategoryId=${encodeURIComponent(
                                          product.parentCategoryId
                                        )}`
                                      : ""
                                  }`
                                : `/products?category=${encodeURIComponent(
                                    product.categoryName
                                  )}${
                                    product.parentCategoryName
                                      ? `&parentCategory=${encodeURIComponent(
                                          product.parentCategoryName
                                        )}`
                                      : ""
                                  }`
                            }
                          >
                            {product.categoryName}
                          </Link>
                        </>
                      )}

                      <span>/</span>
                      <strong>
                        {product.name}
                      </strong>
                    </nav>

                    <div className="kj-pdp-hero__layout">
                      <div className="kj-pdp-gallery">
                        <div className="kj-pdp-main-image">
                          {getImageSource(
                            selectedImage
                          ) ? (
                            <img
                              src={getImageSource(
                                selectedImage
                              )}
                              alt={
                                selectedImage?.alt ||
                                product.name
                              }
                            />
                          ) : (
                            <div className="kj-pdp-image-placeholder">
                              <span>KJ</span>
                              <small>
                                King Jades
                              </small>
                            </div>
                          )}

                          <span className="kj-pdp-gallery__label">
                            Selected Piece
                          </span>

                          {discountPercent >
                            0 && (
                            <span className="kj-pdp-sale-badge">
                              -{discountPercent}%
                            </span>
                          )}
                        </div>

                        {sortedImages.length >
                          1 && (
                          <div className="kj-pdp-thumbnails">
                            {sortedImages.map(
                              (
                                image,
                                index
                              ) => (
                                <button
                                  type="button"
                                  key={`${image.url}-${index}`}
                                  className={
                                    selectedImage?.url ===
                                    image.url
                                      ? "active"
                                      : ""
                                  }
                                  onClick={() =>
                                    setSelectedImage(
                                      image
                                    )
                                  }
                                  aria-label={`Xem ảnh ${
                                    index + 1
                                  }`}
                                >
                                  <img
                                    src={getImageSource(
                                      image
                                    )}
                                    alt={
                                      image.alt ||
                                      product.name
                                    }
                                  />
                                </button>
                              )
                            )}
                          </div>
                        )}
                      </div>

                      <article className="kj-pdp-summary">
                        <span className="kj-pdp-eyebrow">
                          {product.parentCategoryName
                            ? `${product.parentCategoryName} › ${
                                product.categoryName ||
                                "King Jades"
                              }`
                            : product.categoryName ||
                              "King Jades Collection"}
                        </span>

                        <h1>{product.name}</h1>

                        <p className="kj-pdp-summary__description">
                          {product.shortDescription ||
                            "Thông tin giới thiệu sản phẩm đang được cập nhật."}
                        </p>

                        {product.tags?.length >
                          0 && (
                          <div className="kj-pdp-tags">
                            {product.tags
                              .slice(0, 6)
                              .map((tag) => (
                                <span key={tag}>
                                  {tag}
                                </span>
                              ))}
                          </div>
                        )}

                        <div className="kj-pdp-price-card">
                          <div>
                            <span>
                              Giá sản phẩm
                            </span>

                            <strong>
                              {formatPrice(
                                finalPrice
                              )}
                            </strong>

                            {product.salePrice >
                              0 &&
                              product.price >
                                0 && (
                                <del>
                                  {formatPrice(
                                    product.price
                                  )}
                                </del>
                              )}
                          </div>

                          <p>
                            Giá và tình trạng được
                            xác nhận lại trước khi
                            tư vấn hoặc đặt hàng.
                          </p>
                        </div>

                        <dl className="kj-pdp-meta">
                          <div>
                            <dt>Mã sản phẩm</dt>
                            <dd>
                              {product.sku ||
                                "Đang cập nhật"}
                            </dd>
                          </div>

                          {
                            dynamicSpecifications
                              .slice(0, 3)
                              .map((item, index) => (
                                <div key={`${item.label}-${index}`}>
                                  <dt>
                                    {item.label}
                                  </dt>
                                  <dd>
                                    {item.value}
                                  </dd>
                                </div>
                              ))
                          }
                        </dl>

                        {addedMessage && (
                          <div
                            className="kj-pdp-added-message"
                            role="status"
                          >
                            {addedMessage}
                          </div>
                        )}

                        <div className="kj-pdp-actions">
                          <button
                            type="button"
                            className="kj-pdp-button kj-pdp-button--gold"
                            onClick={
                              handleAddToCart
                            }
                          >
                            <BagIcon />
                            Thêm vào giỏ quan tâm
                          </button>

                          <Link
                            to={contactUrl}
                            className="kj-pdp-button kj-pdp-button--navy"
                          >
                            Liên hệ báo giá
                            <ArrowIcon />
                          </Link>

                          <a
                            href={ZALO_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="kj-pdp-button kj-pdp-button--light"
                          >
                            <MessageIcon />
                            Tư vấn Zalo
                          </a>
                        </div>

                        <p className="kj-pdp-summary__note">
                          Gửi yêu cầu không đồng
                          nghĩa với hoàn tất đặt
                          hàng hoặc thanh toán.
                        </p>
                      </article>
                    </div>
                  </div>
                </section>

                <section className="kj-pdp-highlights">
                  <div className="kj-pdp-shell kj-pdp-highlights__grid">
                    {careCards.map(
                      (card) => (
                        <article
                          key={card.number}
                        >
                          <span>
                            {card.number}
                          </span>

                          <div>
                            <strong>
                              {card.title}
                            </strong>
                            <p>{card.text}</p>
                          </div>
                        </article>
                      )
                    )}
                  </div>
                </section>

                <section className="kj-pdp-information">
                  <div className="kj-pdp-shell kj-pdp-information__layout">
                    <div className="kj-pdp-description">
                      <span className="kj-pdp-eyebrow">
                        Product Story
                      </span>

                      <h2>
                        Mô tả sản phẩm
                      </h2>

                      <p>
                        {product.description ||
                          product.shortDescription ||
                          "Nội dung mô tả chi tiết đang được cập nhật."}
                      </p>
                    </div>

                    <div className="kj-pdp-accordions">
                      <details open>
                        <summary>
                          <span>
                            Chất liệu và thông số
                          </span>
                          <b>+</b>
                        </summary>

                        <div>
                          {product
                            .specifications
                            ?.length > 0 ? (
                            <dl className="kj-pdp-specifications">
                              {product.specifications.map(
                                (
                                  specification,
                                  index
                                ) => (
                                  <div
                                    key={`${specification.label}-${index}`}
                                  >
                                    <dt>
                                      {
                                        specification.label
                                      }
                                    </dt>
                                    <dd>
                                      {
                                        specification.value
                                      }
                                    </dd>
                                  </div>
                                )
                              )}
                            </dl>
                          ) : (
                            <p>
                              Thông số sản phẩm
                              đang được cập nhật.
                            </p>
                          )}
                        </div>
                      </details>

                      <details>
                        <summary>
                          <span>
                            Kiểm định và hồ sơ
                          </span>
                          <b>+</b>
                        </summary>

                        <div>
                          <p>
                            {certificationText}
                          </p>
                        </div>
                      </details>

                      <details>
                        <summary>
                          <span>
                            Tư vấn size vòng
                          </span>
                          <b>+</b>
                        </summary>

                        <div>
                          <p>
                            King Jades hỗ trợ
                            chọn size theo kích
                            thước cổ tay, bàn tay
                            và cảm giác đeo mong
                            muốn.
                          </p>

                          <a href="#size-guide">
                            Xem hướng dẫn đo size
                          </a>
                        </div>
                      </details>

                      <details>
                        <summary>
                          <span>
                            Giao nhận và hỗ trợ
                          </span>
                          <b>+</b>
                        </summary>

                        <div>
                          <p>
                            Thời gian giao nhận,
                            phạm vi hỗ trợ và
                            chính sách áp dụng sẽ
                            được xác nhận trong
                            quá trình tư vấn.
                          </p>
                        </div>
                      </details>
                    </div>
                  </div>
                </section>

                <section
                  id="size-guide"
                  className="kj-pdp-size"
                >
                  <div className="kj-pdp-shell kj-pdp-size__layout">
                    <div className="kj-pdp-size__copy">
                      <span className="kj-pdp-eyebrow">
                        Size Guidance
                      </span>

                      <h2>
                        Chưa chắc size vòng nào
                        phù hợp?
                      </h2>

                      <p>
                        Vòng ngọc có cảm giác đeo
                        khác với vòng kim loại.
                        Chuẩn bị số đo hoặc ảnh
                        vòng đang đeo để chuyên
                        viên đối chiếu trước khi
                        tư vấn.
                      </p>

                      <Link to={contactUrl}>
                        Nhận tư vấn size
                        <ArrowIcon />
                      </Link>
                    </div>

                    <div className="kj-pdp-size__steps">
                      <article>
                        <span>01</span>
                        <RulerIcon />
                        <h3>
                          Đo phần rộng nhất của
                          bàn tay
                        </h3>
                        <p>
                          Khép các ngón tay và đo
                          quanh vị trí rộng nhất.
                        </p>
                      </article>

                      <article>
                        <span>02</span>
                        <RulerIcon />
                        <h3>
                          Đo chu vi cổ tay
                        </h3>
                        <p>
                          Dùng thước dây mềm đo
                          sát phần xương cổ tay.
                        </p>
                      </article>

                      <article>
                        <span>03</span>
                        <MessageIcon />
                        <h3>
                          Gửi hình ảnh tham khảo
                        </h3>
                        <p>
                          Gửi ảnh vòng đang đeo
                          vừa để đội ngũ so sánh.
                        </p>
                      </article>
                    </div>
                  </div>
                </section>

                <section className="kj-pdp-after-sale">
                  <div className="kj-pdp-shell">
                    <header className="kj-pdp-section-heading">
                      <div>
                        <span className="kj-pdp-eyebrow">
                          After Sales Service
                        </span>

                        <h2>
                          Đồng hành sau khi bàn
                          giao sản phẩm.
                        </h2>
                      </div>

                      <p>
                        Quy trình tiếp nhận nhu
                        cầu sau mua được trình
                        bày rõ để khách hàng dễ
                        theo dõi.
                      </p>
                    </header>

                    <div className="kj-pdp-after-sale__grid">
                      {afterSaleSteps.map(
                        (step) => (
                          <article
                            key={step.number}
                          >
                            <span>
                              {step.number}
                            </span>

                            <ShieldIcon />

                            <h3>
                              {step.title}
                            </h3>

                            <p>
                              {step.text}
                            </p>
                          </article>
                        )
                      )}
                    </div>
                  </div>
                </section>

                <section className="kj-pdp-cta">
                  <div className="kj-pdp-shell kj-pdp-cta__inner">
                    <div>
                      <span className="kj-pdp-eyebrow">
                        Personalized Guidance
                      </span>

                      <h2>
                        Cần xem thêm hình ảnh hoặc
                        tư vấn trực tiếp?
                      </h2>

                      <p>
                        Gửi mã sản phẩm và nhu cầu
                        để đội ngũ King Jades hỗ
                        trợ riêng.
                      </p>
                    </div>

                    <Link to={contactUrl}>
                      Liên hệ tư vấn
                      <ArrowIcon />
                    </Link>
                  </div>
                </section>

                <div className="kj-pdp-mobile-bar">
                  <div>
                    <small>
                      {product.name}
                    </small>
                    <strong>
                      {formatPrice(finalPrice)}
                    </strong>
                  </div>

                  <button
                    type="button"
                    onClick={handleAddToCart}
                  >
                    <BagIcon />
                    Thêm vào giỏ
                  </button>
                </div>
              </>
            )}
        </main>
      </div>

      <Footer />
    </>
  );
};

export default ProductDetailPage;
